import axios from "axios";
import {
  AUTH_EXPIRES_AT_STORAGE_KEY,
  AUTH_STORAGE_VERSION_KEY,
  AUTH_TOKEN_STORAGE_KEY,
  type AuthSessionMode,
  persistAuthSessionMode,
} from "@/lib/storage";
import { getCheckoutAttributionSnapshot, type AttributionTouch } from "@/lib/attribution";

export const PRODUCTION_API_BASE_URL = "https://api.justicedrive.co";

const RAW_API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").trim();
const LOCALHOST_URL_RE = /^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?/i;
export const resolveApiBaseUrl = (rawValue: string | undefined, isDev: boolean): string => {
  const rawApiBaseUrl = (rawValue ?? "").trim();
  if (!rawApiBaseUrl) {
    return isDev ? "http://localhost:8000" : PRODUCTION_API_BASE_URL;
  }
  if (!isDev && LOCALHOST_URL_RE.test(rawApiBaseUrl)) {
    return "";
  }
  return rawApiBaseUrl;
};

export const API_BASE_URL = resolveApiBaseUrl(RAW_API_BASE_URL, import.meta.env.DEV);

const AUTH_STORAGE_VERSION = "2026-04-25-session-reset-1";

const apiClient = axios.create({
  baseURL: API_BASE_URL || undefined,
  timeout: 60_000
});
const authClient = axios.create({
  baseURL: API_BASE_URL || undefined,
  timeout: 60_000
});

let tokenBootstrapPromise: Promise<string> | null = null;
let attributionSyncKey: string | null = null;

const bootstrapAuthStorageVersion = (): void => {
  try {
    const currentVersion = localStorage.getItem(AUTH_STORAGE_VERSION_KEY);
    if (currentVersion === AUTH_STORAGE_VERSION) {
      return;
    }
    // Keep existing auth tokens across deploys; clearing here breaks Stripe return ownership.
    localStorage.setItem(AUTH_STORAGE_VERSION_KEY, AUTH_STORAGE_VERSION);
  } catch {
    // Ignore storage access issues.
  }
};

bootstrapAuthStorageVersion();

type RequestOptions = {
  data?: unknown;
  method: "GET" | "POST";
  path: string;
  responseType?: "arraybuffer" | "blob" | "json" | "text";
};

export type PremiumProductKey =
  | "court_pdf"
  | "records_request"
  | "mail_filing"
  | "premium_defense";

export interface CheckoutRecoveryVerifyResult {
  access_token: string;
  expires_at: string;
  ticket_id: string;
  product_key: PremiumProductKey;
  offer_type: "normal" | "save_offer";
  purchased: boolean;
  return_path: string;
}

export interface PremiumProductMetadata {
  badge: string | null;
  bullets: string[];
  checkout_url: string | null;
  compare_at_cents: number | null;
  cta_label: string | null;
  currency: string | null;
  price_cents: number | null;
  price_id: string | null;
  product_key: PremiumProductKey | null;
  subtitle: string | null;
  title: string | null;
}

export interface PremiumLockedPayload {
  checkout_url: string | null;
  code: "premium_locked";
  locked: true;
  message: string | null;
  price: string | null;
  price_id: string | null;
  product_key: PremiumProductKey | null;
  product: PremiumProductMetadata | null;
  reason: string | null;
  unlocks: Record<string, boolean>;
}

export class PremiumLockedError extends Error {
  detail: PremiumLockedPayload;
  status: number;

  constructor(detail: PremiumLockedPayload, status = 402) {
    super(detail.message || detail.reason || "Premium access required.");
    this.name = "PremiumLockedError";
    this.detail = detail;
    this.status = status;
  }
}

export interface ApiErrorDetail {
  code: string | null;
  message: string | null;
  metadata: Record<string, unknown> | null;
}

export class ApiRequestError extends Error {
  detail: ApiErrorDetail | null;
  status: number | null;

  constructor(message: string, options?: { detail?: ApiErrorDetail | null; status?: number | null }) {
    super(message);
    this.name = "ApiRequestError";
    this.detail = options?.detail ?? null;
    this.status = options?.status ?? null;
  }
}

export const isApiRequestError = (error: unknown): error is ApiRequestError => error instanceof ApiRequestError;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const asNullableString = (value: unknown): string | null => (typeof value === "string" && value.trim() ? value : null);

const asNullableNumber = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0) : [];

const asProductKey = (value: unknown): PremiumProductKey | null =>
  value === "court_pdf" || value === "records_request" || value === "mail_filing" || value === "premium_defense"
    ? value
    : null;

const normalizePremiumProduct = (value: unknown): PremiumProductMetadata | null => {
  const raw = asRecord(value);
  if (!raw) {
    return null;
  }
  return {
    badge: asNullableString(raw.badge),
    bullets: asStringArray(raw.bullets),
    checkout_url: asNullableString(raw.checkout_url),
    compare_at_cents: asNullableNumber(raw.compare_at_cents),
    cta_label: asNullableString(raw.cta_label),
    currency: asNullableString(raw.currency),
    price_cents: asNullableNumber(raw.price_cents),
    price_id: asNullableString(raw.price_id),
    product_key: asProductKey(raw.product_key),
    subtitle: asNullableString(raw.subtitle),
    title: asNullableString(raw.title),
  };
};

const normalizePremiumLockedPayload = (value: unknown): PremiumLockedPayload | null => {
  const raw = asRecord(value);
  if (!raw || raw.code !== "premium_locked" || raw.locked !== true) {
    return null;
  }
  return {
    checkout_url: asNullableString(raw.checkout_url),
    code: "premium_locked",
    locked: true,
    message: asNullableString(raw.message),
    price: asNullableString(raw.price),
    price_id: asNullableString(raw.price_id),
    product_key: asProductKey(raw.product_key),
    product: normalizePremiumProduct(raw.product),
    reason: asNullableString(raw.reason),
    unlocks: asRecord(raw.unlocks) ?? {},
  };
};

const normalizeApiErrorDetail = (value: unknown): ApiErrorDetail | null => {
  const raw = asRecord(value);
  if (!raw) {
    return null;
  }
  return {
    code: asNullableString(raw.code),
    message: asNullableString(raw.message),
    metadata: asRecord(raw.metadata),
  };
};

export const isPremiumLockedError = (error: unknown): error is PremiumLockedError => error instanceof PremiumLockedError;

async function request<TResponse>(options: RequestOptions): Promise<TResponse> {
  const response = await apiClient.request<TResponse>({
    data: options.data,
    method: options.method,
    responseType: options.responseType ?? "json",
    url: options.path
  });
  return response.data;
}

type GuestSessionResponse = {
  access_token: string;
  expires_at: string;
  token_type: string;
  user_id: string;
};

const getStoredToken = (): string | null => {
  const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  const expiresAt = localStorage.getItem(AUTH_EXPIRES_AT_STORAGE_KEY);
  if (!token || !expiresAt) {
    return null;
  }
  const expiresMs = Date.parse(expiresAt);
  if (Number.isNaN(expiresMs) || Date.now() >= expiresMs - 10_000) {
    return null;
  }
  return token;
};

const storeToken = (token: string, expiresAt: string): void => {
  localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
  localStorage.setItem(AUTH_EXPIRES_AT_STORAGE_KEY, expiresAt);
};

const syncStoredAttributionWithToken = (token: string): void => {
  if (import.meta.env.MODE === "test") return;
  const snapshot = getCheckoutAttributionSnapshot();
  const touch = snapshot?.last_touch ?? snapshot?.first_touch;
  if (!touch) return;
  const nextKey = `${token.slice(0, 24)}:${JSON.stringify(touch)}`;
  if (nextKey === attributionSyncKey) return;
  attributionSyncKey = nextKey;
  const hasClickId = Boolean(touch.gclid || touch.gbraid || touch.wbraid || touch.msclkid);
  void authClient
    .post(
      "/api/v1/attribution/capture",
      { consent: hasClickId ? "advertising" : "analytics", touch },
      { headers: { Authorization: `Bearer ${token}` } },
    )
    .catch(() => {
      attributionSyncKey = null;
    });
};

const inferAuthSessionModeFromToken = (token: string): AuthSessionMode => {
  try {
    const [, payload] = token.split(".");
    if (!payload) {
      return "guest";
    }
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(window.atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="))) as {
      subject_type?: unknown;
    };
    return decoded.subject_type === "account" ? "account" : "guest";
  } catch {
    return "guest";
  }
};

export const applyAccessToken = (token: string, expiresAt: string): void => {
  storeToken(token, expiresAt);
  persistAuthSessionMode(inferAuthSessionModeFromToken(token));
  syncStoredAttributionWithToken(token);
};

const createGuestSession = async (): Promise<string> => {
  const response = await authClient.post<GuestSessionResponse>("/api/v1/auth/guest-session", {});
  storeToken(response.data.access_token, response.data.expires_at);
  persistAuthSessionMode("guest");
  syncStoredAttributionWithToken(response.data.access_token);
  return response.data.access_token;
};

const ensureAccessToken = async (forceRefresh = false): Promise<string> => {
  if (!forceRefresh) {
    const token = getStoredToken();
    if (token) {
      syncStoredAttributionWithToken(token);
      return token;
    }
  }
  if (!tokenBootstrapPromise) {
    tokenBootstrapPromise = createGuestSession().finally(() => {
      tokenBootstrapPromise = null;
    });
  }
  return tokenBootstrapPromise;
};

apiClient.interceptors.request.use(async (config) => {
  const token = await ensureAccessToken(false);
  config.headers = config.headers ?? ({} as import("axios").AxiosRequestHeaders);
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const requestIdHeader =
      error?.response?.headers?.["x-request-id"] ??
      error?.response?.headers?.["X-Request-Id"] ??
      null;
    const originalConfig = error?.config as
      | (Record<string, unknown> & { headers?: Record<string, string>; __authRetried?: boolean })
      | undefined;
    if (status === 401 && originalConfig && !originalConfig.__authRetried) {
      originalConfig.__authRetried = true;
      const token = await ensureAccessToken(true);
      originalConfig.headers = originalConfig.headers ?? {};
      originalConfig.headers.Authorization = `Bearer ${token}`;
      return apiClient.request(originalConfig);
    }
    const premiumLocked = normalizePremiumLockedPayload(detail);
    if ((status === 402 || status === 403) && premiumLocked) {
      return Promise.reject(new PremiumLockedError(premiumLocked, status));
    }
    if (typeof detail === "string" && detail.trim()) {
      return Promise.reject(
        new ApiRequestError(detail, {
          status,
          detail: {
            code: null,
            message: detail,
            metadata: requestIdHeader ? { request_id: String(requestIdHeader) } : null
          }
        })
      );
    }
    const apiDetail = normalizeApiErrorDetail(detail);
    const withRequestId: ApiErrorDetail | null = apiDetail
      ? {
          ...apiDetail,
          metadata: {
            ...(apiDetail.metadata ?? {}),
            ...(requestIdHeader ? { request_id: String(requestIdHeader) } : {})
          }
        }
      : requestIdHeader
      ? { code: null, message: null, metadata: { request_id: String(requestIdHeader) } }
      : null;
    if (apiDetail?.message) {
      return Promise.reject(new ApiRequestError(apiDetail.message, { detail: withRequestId ?? apiDetail, status }));
    }
    if (withRequestId) {
      return Promise.reject(new ApiRequestError("Request failed.", { detail: withRequestId, status }));
    }
    return Promise.reject(error instanceof Error ? error : new Error("Request failed."));
  }
);

export interface AnalyzeTicketRequest {
  source_state?: "CA" | "FL" | "NY" | "NJ" | "TX";
  florida_county?: string;
  ny_jurisdiction?: string;
  citation_id?: string;
  citation_notes?: string;
  court_name?: string;
  court_address?: string;
  court_date?: string;
  court_time?: string;
  court_phone?: string;
  defendant_full_name?: string;
  issuing_agency?: string;
  officer_name?: string;
  officer_id?: string;
  violation_description?: string;
  mvars_checked?: boolean | null;
  age?: number | null;
  vehicle_make?: string;
  vehicle_color?: string;
  vehicle_body_style?: string;
  vehicle_type?: string;
  insurance_company?: string;
  policy_number?: string;
  mailing_address?: string;
  ocr_raw_text?: string;
  ocr_confidence?: number;
  ocr_snapshot?: {
    raw_text?: string;
    citation_id?: string | null;
    violation_code?: string | null;
    violation_description?: string | null;
    violation_code_raw?: string | null;
    normalized_offense_type?: string | null;
    court_name?: string | null;
    defendant_name?: string | null;
    location_text?: string | null;
    issue_date?: string | null;
    issue_time?: string | null;
    issuing_agency?: string | null;
    confidence?: number;
    ocr_run_id?: string | null;
    storage_ref?: string | null;
    source_marker?: "real_backend" | "fallback" | "mock" | "derived";
  };
  upload_trace?: {
    storage_ref?: string | null;
    file_name?: string | null;
    mime_type?: string | null;
    size_bytes?: number | null;
    source_marker?: "real_backend" | "fallback" | "mock" | "derived";
  };
  user_story?: string;
  user_review_confirmed?: boolean;
  user_review_confirmed_at?: string;
  field_confirmations?: Array<{
    field_key:
      | "citation_id"
      | "violation_code"
      | "court_name"
      | "court_date"
      | "court_time"
      | "court_address"
      | "court_phone"
      | "violation_location"
      | "violation_timestamp"
      | "issue_date"
      | "issue_time"
      | "defendant_full_name"
      | "issuing_agency"
      | "officer_name"
      | "officer_id"
      | "mvars_checked"
      | "age"
      | "vehicle_make"
      | "vehicle_color"
      | "vehicle_body_style"
      | "vehicle_type"
      | "insurance_company"
      | "policy_number";
    original_ocr_value?: string | null;
    final_user_value?: string | null;
    was_corrected?: boolean;
    correction_source?: "user_confirmed" | "user_corrected" | "ocr_confirmed" | "user_added";
    confirmed_at?: string;
  }>;
  user_corrections?: Array<{
    field_key: string;
    original_value?: string | null;
    corrected_value?: string | null;
    correction_timestamp?: string;
    source?: "user_confirmed" | "user_corrected" | "ocr_confirmed" | "user_added";
  }>;
  violation_code?: string;
  violation_latitude?: number;
  violation_longitude?: number;
  violation_location?: string;
  violation_timestamp?: string;
  issue_date?: string;
  issue_time?: string;
}

export interface GeoAnalyzeLocationResult {
  city: string | null;
  confidence: number;
  county: string | null;
  ets_prompt: string | null;
  ets_required: boolean;
  formatted_address: string | null;
  input_location?: string | null;
  latitude: number | null;
  location_query: string;
  longitude: number | null;
  map_safe?: boolean;
  maps_url: string | null;
  normalized_location: string | null;
  posted_speed_limit_mph: number | null;
  provider?: "google" | "mapbox" | "none";
  provider_query?: string | null;
  rejected_result?: string | null;
  rejection_reason?: string | null;
  road_name: string | null;
  source: string;
  static_map_url: string | null;
  state?: string | null;
  validation_flags?: string[];
  validation_status?: "accepted" | "needs_confirmation" | "rejected";
  zone_type: string | null;
}

export interface WeatherContextResult {
  adverse_for_speed_measurement: boolean;
  cloud_cover: number | null;
  condition_summary: string | null;
  confidence: number;
  formatted_address: string | null;
  location_query: string | null;
  narrative: string | null;
  observed_at_utc: string | null;
  precipitation: number;
  source: string;
  sunrise: string | null;
  sunset: string | null;
  sun_glare_risk: boolean;
  temperature_f: number | null;
  visibility_miles: number | null;
  wind_speed_mph: number | null;
}

export interface AnalyzeTicketResult {
  application: string;
  argument_body: string;
  source_state?: string | null;
  florida_county?: string | null;
  florida_classification_status?: "eligible" | "excluded" | "needs_review" | "not_applicable" | null;
  florida_classification_reason?: string | null;
  florida_checkout_available?: boolean;
  ny_jurisdiction?: string | null;
  ny_classification_status?: "eligible" | "excluded" | "needs_review" | "not_applicable" | null;
  ny_classification_reason?: string | null;
  ny_checkout_available?: boolean;
  ny_official_resource_label?: string | null;
  ny_official_resource_url?: string | null;
  citation_facts?: {
    location_text?: { value?: string | null } | null;
    violation_code?: { value?: string | null } | null;
    violation_description?: { value?: string | null } | null;
  };
  citation_category?: {
    citation_category?: string;
    detected_category?: string;
    confidence: number;
    display_label?: string;
    fallback_applied?: boolean;
    signals_used?: string[];
    evidence_basis?: string[];
    procedural_risks?: string[];
    recommended_records?: string[];
  };
  conclusion: string;
  evidence_ledger?: Array<{
    confidence: number;
    field: string;
    rationale: string;
    requires_verification: boolean;
    source:
      | "citation_front_ocr"
      | "citation_back_ocr"
      | "user_confirmation"
      | "heuristic"
      | "external_record"
      | "trusted_external_record"
      | "geocoder"
      | "weather_api";
    status: "verified" | "inferred" | "conditional" | "unknown";
    value: unknown;
  }>;
  evidence_gaps?: Array<{
    gap_type: string;
    label: string;
    why_it_matters: string;
    certainty: "needs_confirmation" | "needs_records" | "not_applicable";
    source: "citation" | "user_corrected" | "external" | "inferred" | "user_context";
    records: string[];
  }>;
  follow_up_questions: string[];
  geo?: GeoAnalyzeLocationResult | null;
  issue: string;
  legal_strength_score: number;
  rule: string;
  ticket_id: string;
  normalized_fields?: Record<string, any>;
  theory_graph?: Array<{
    confidence: number;
    missing_facts: string[];
    required_facts: string[];
    safe_user_message: string;
    state: string;
    status: "active" | "available" | "blocked" | "unknown";
    theory_key: string;
  }>;
  written_contest_eligibility_status?: {
    status: "active" | "conditional" | "blocked";
    safe_message: string;
  };
  device_enforcement_analysis?: {
    automated_enforcement_check: {
      automated_program_detected: boolean;
      confidence: number;
      flags: string[];
      program_compliance_status: string;
      program_type: string;
      provenance?: string | null;
      verified_vs_inferred: string;
    };
    confidence: number;
    device_verification: {
      calibration_status: string;
      expires_on?: string | null;
      last_verification_date?: string | null;
      legal_flags: string[];
      record_confidence: number;
      record_source?: string | null;
      verified_vs_inferred: string;
    };
    enforcement_device: {
      device_id?: string | null;
      device_type: string;
      enforcement_type: string;
      evidence: Array<{
        confidence: number;
        provenance?: string | null;
        signal: string;
        source_type: string;
        value: string;
      }>;
      issuing_agency?: string | null;
      source_confidence: number;
      verified_vs_inferred: string;
    };
    ets_verification: {
      confidence: number;
      jurisdiction_conflict: boolean;
      legal_flags: string[];
      provenance?: string | null;
      required_for_argument: boolean;
      survey_date?: string | null;
      survey_status: string;
      verified_vs_inferred: string;
    };
    program_classification: {
      classification: string;
      confidence: number;
      provenance?: string | null;
      rationale: string;
      verified_vs_inferred: string;
    };
    recommended_actions: string[];
    summary: string;
  };
  weather?: WeatherContextResult | null;
  metadata?: {
    retrieval_degraded: boolean;
    retrieval_mode: string;
    retrieval_notice?: string | null;
  };
  defense_angles?: Array<{
    type: string;
    title: string;
    strength: "low" | "medium" | "high";
    basis: string;
    evidence_status: "verified" | "inferred" | "records_required";
    recommended_action: string;
    safe_user_copy: string;
    admin_debug?: Record<string, unknown>;
  }>;
  legal_accuracy_audit?: {
    claim_count: number;
    verified_claims: number;
    inferred_claims: number;
    records_required_claims: number;
    unsupported_claims: number;
    risk_level: "safe" | "caution" | "unsafe";
    items?: Array<{
      claim_text: string;
      status: string;
      source_type?: string | null;
      source_ref?: string | null;
      risk_terms?: string[];
      suggested_safe_wording?: string | null;
    }>;
  };
  records_request_plan?: {
    item_count: number;
    high_priority_count: number;
    items: Array<{
      record_type: string;
      title: string;
      reason: string;
      target_agency?: string | null;
      priority: "low" | "medium" | "high";
      certainty?: "needs_confirmation" | "needs_records" | null;
      linked_defense_angle?: string | null;
      suggested_request_text: string;
      evidence_status: "verified" | "inferred" | "records_required";
      admin_debug?: Record<string, unknown>;
    }>;
  };
  traffic_survey_check?: {
    applicable: boolean;
    status: "not_applicable" | "survey_found" | "records_required" | "insufficient_location" | "unsupported";
    survey_found: boolean;
    location_segment?: string | null;
    statute?: string | null;
    reason: string;
    recommended_action?: string | null;
    records_needed: string[];
    evidence_status: "verified" | "inferred" | "records_required";
    admin_debug?: Record<string, unknown>;
  };
}

export interface OcrProcessResult {
  ticket_id?: string | null;
  state?: string | null;
  citation_id: string | null;
  confidence: number;
  court_name: string | null;
  court_appearance?: {
    court_date?: string | null;
    court_time?: string | null;
    court_name?: string | null;
    court_address?: string | null;
    court_phone?: string | null;
  } | null;
  defendant_name: string | null;
  location_text: string | null;
  mailing_address?: string | null;
  violation_datetime?: string | null;
  issue_date?: string | null;
  issue_time?: string | null;
  issuing_agency?: string | null;
  officer_name?: string | null;
  officer_id?: string | null;
  mvars_checked?: boolean | null;
  age?: number | null;
  vehicle_make?: string | null;
  vehicle_color?: string | null;
  vehicle_body_style?: string | null;
  vehicle_type?: string | null;
  insurance_company?: string | null;
  policy_number?: string | null;
  parser_debug?: {
    field_review?: Record<string, { status?: "verified" | "needs_review" | "missing" | "inferred"; present?: boolean; reason?: string }>;
    court_appearance?: Record<string, string>;
    [key: string]: unknown;
  } | null;
  ocr_run_id?: string | null;
  extraction_status?: "ok" | "needs_manual_entry" | string;
  warning?: string | null;
  warnings?: string[] | null;
  normalized_fields?: Record<string, unknown> | null;
  provider_trace?: Record<string, unknown> | null;
  raw_text: string;
  source_marker?: "real_backend" | "fallback" | "mock" | "derived";
  storage_ref?: string | null;
  upload_trace?: {
    storage_ref?: string | null;
    file_name?: string | null;
    mime_type?: string | null;
    size_bytes?: number | null;
    source_marker?: "real_backend" | "fallback" | "mock" | "derived";
  } | null;
  violation_code: string | null;
  violation_description?: string | null;
  extracted_fields?: Record<string, unknown> | null;
}

export interface MailStatusResult {
  history: Array<Record<string, unknown>>;
  lifecycle_state:
    | "draft_ready"
    | "awaiting_address_review"
    | "ready_to_mail"
    | "mailed"
    | "tracking_available"
    | "delivery_failed"
    | "manual_review_required"
    | "mail_not_purchased";
  last_event: string | null;
  last_provider_event?: string | null;
  lob_id: string | null;
  current_status?: string | null;
  user_message?: string | null;
  source_of_truth?: "real_backend" | string | null;
  provider_name?: string | null;
  provider_environment?: string | null;
  provider_status?: string | null;
  failure_code?: string | null;
  tracking_available?: boolean;
  delivery_confirmed?: boolean;
  filing_destination?: {
    court_name?: string | null;
    court_mailing_address?: string | null;
    address_source?: "extracted_from_citation" | "user_confirmed" | "admin_verified" | "missing" | "unverified";
    filing_method?: "Certified Mail" | "Manual Filing" | "Draft Only";
    eligibility?: "active" | "blocked" | "needs_review";
  } | null;
  defendant_address_status?: {
    state?:
      | "valid"
      | "invalid_defendant_address"
      | "invalid_court_address"
      | "incomplete_address"
      | "unsupported_destination"
      | "manual_review_required"
      | "missing"
      | "unverified";
    message?: string | null;
    source?: string | null;
  } | null;
  court_address_status?: {
    state?:
      | "valid"
      | "invalid_defendant_address"
      | "invalid_court_address"
      | "incomplete_address"
      | "unsupported_destination"
      | "manual_review_required"
      | "missing"
      | "unverified";
    message?: string | null;
    source?: string | null;
  } | null;
  timeline_events?: Array<{
    event_type: string;
    provider_status: string | null;
    normalized_status: string;
    tracking_number: string | null;
    lob_id: string | null;
    occurred_at: string | null;
  }>;
  delivered_at?: string | null;
  returned_at?: string | null;
  failure_reason?: string | null;
  sent_at: string | null;
  status: string;
  ticket_id: string;
  tracking_number: string | null;
  updated_at: string | null;
}

export interface MailSendResult {
  lob_id: string | null;
  pdf_file_name: string;
  sent_at: string;
  status: string;
  lifecycle_state: MailStatusResult["lifecycle_state"];
  user_message: string;
  source_of_truth?: "real_backend" | string | null;
  provider_name?: string | null;
  provider_environment?: string | null;
  provider_status?: string | null;
  filing_destination?: MailStatusResult["filing_destination"];
  defendant_address_status?: MailStatusResult["defendant_address_status"];
  court_address_status?: MailStatusResult["court_address_status"];
  ticket_id: string;
  tracking_number: string | null;
  updated_at: string;
}

export interface MailAddressCorrectionPayload {
  city?: string;
  state?: string;
  street?: string;
  zip?: string;
}

export interface RecordsRequestArtifactResult {
  body: string;
  county: string | null;
  court_name: string | null;
  generated_at: string | null;
  offense_context: string;
  requested_records: string[];
  ticket_id: string;
  title: string;
}

export type RecordsWorkflowStatus =
  | "draft_generated"
  | "review_required"
  | "user_confirmed"
  | "manual_pending"
  | "provider_submitted"
  | "provider_confirmed"
  | "failed";

export interface RecordsWorkflowStatusResult {
  created_at: string | null;
  disclaimer: string;
  draft_body: string | null;
  draft_file_path: string | null;
  draft_title: string | null;
  error_message: string | null;
  provider_name: string | null;
  provider_reference: string | null;
  status: RecordsWorkflowStatus | null;
  submitted_at: string | null;
  target_label: string | null;
  ticket_id: string;
  updated_at: string | null;
  workflow_state: "not_started" | "active";
}

export interface RecordsWorkflowManualPendingResult extends RecordsWorkflowStatusResult {
  instructions: string;
}

export interface CaseStatusResult {
  analysis_status: string;
  current_stage: string;
  result_status: string;
}

export interface PremiumStatusResult {
  checkout_session_id: string | null;
  locked: boolean;
  payment_status: string | null;
  reason: string;
  unlocked: boolean;
  unlocks: Record<string, boolean>;
  product_entitlements?: Record<string, boolean>;
}

export interface PurchaseItemResult {
  currency: string | null;
  price: number | null;
  product_key: PremiumProductKey;
  product_name: string;
  purchased_at: string | null;
  status: string;
  stripe_session_id: string | null;
}

export interface TicketEntitlementResult {
  checkout_session_id: string | null;
  checkout_url: string | null;
  locked: boolean;
  payment_status: string | null;
  price: string | null;
  price_id: string | null;
  product_key: PremiumProductKey | null;
  product: PremiumProductMetadata | null;
  reason: string | null;
  ticket_id: string;
  unlocks: Record<string, boolean>;
}

export interface ArtifactStatusItemResult {
  access: "free" | "premium";
  available: boolean;
  blocked: boolean;
  detail: string | null;
  generated: boolean;
  purchased?: boolean;
  action?: string | null;
  lob_id?: string | null;
  raw_status?: string | null;
  route: string | null;
  state: string;
  tracking_number?: string | null;
  updated_at: string | null;
}

export interface ArtifactStatusResult {
  mail: ArtifactStatusItemResult;
  premium_defense: ArtifactStatusItemResult;
  pdf: ArtifactStatusItemResult;
  records: ArtifactStatusItemResult;
}

export interface CaseDetailResult {
  analysis: AnalyzeTicketResult;
  artifact_status: ArtifactStatusResult;
  case_status: CaseStatusResult;
  input: {
    citation_id?: string | null;
    court_address?: string | null;
    defendant_full_name?: string | null;
    violation_code?: string | null;
    violation_location?: string | null;
    violation_timestamp?: string | null;
  };
  premium_status: PremiumStatusResult;
  product_entitlements?: Record<string, boolean>;
  purchases?: PurchaseItemResult[];
  summary: {
    citation_id?: string | null;
    conclusion_preview?: string | null;
    created_at?: string | null;
    has_geo?: boolean;
    has_weather?: boolean;
    legal_strength_score?: number;
    ticket_id: string;
    updated_at?: string | null;
    violation_code?: string | null;
  };
}

const normalizeArtifactStatusItem = (value: unknown): ArtifactStatusItemResult => {
  const raw = asRecord(value);
  return {
    access: raw?.access === "free" ? "free" : "premium",
    available: raw?.available === true,
    blocked: raw?.blocked !== false,
    detail: asNullableString(raw?.detail),
    generated: raw?.generated === true,
    purchased: raw?.purchased === true,
    action: asNullableString(raw?.action),
    lob_id: asNullableString(raw?.lob_id),
    raw_status: asNullableString(raw?.raw_status),
    route: asNullableString(raw?.route),
    state: asNullableString(raw?.state) ?? "unknown",
    tracking_number: asNullableString(raw?.tracking_number),
    updated_at: asNullableString(raw?.updated_at),
  };
};

const normalizeArtifactStatus = (value: unknown): ArtifactStatusResult => {
  const raw = asRecord(value);
  return {
    mail: normalizeArtifactStatusItem(raw?.mail),
    premium_defense: normalizeArtifactStatusItem(raw?.premium_defense),
    pdf: normalizeArtifactStatusItem(raw?.pdf),
    records: normalizeArtifactStatusItem(raw?.records),
  };
};

export const normalizeCaseDetailResult = (value: CaseDetailResult | Record<string, unknown>): CaseDetailResult => {
  const raw = asRecord(value) ?? {};
  const summary = asRecord(raw.summary) ?? {};
  const premiumStatus = asRecord(raw.premium_status) ?? {};
  const caseStatus = asRecord(raw.case_status) ?? {};
  const input = asRecord(raw.input) ?? {};

  return {
    analysis: raw.analysis as AnalyzeTicketResult,
    artifact_status: normalizeArtifactStatus(raw.artifact_status),
    case_status: {
      analysis_status: asNullableString(caseStatus.analysis_status) ?? "unknown",
      current_stage: asNullableString(caseStatus.current_stage) ?? "unknown",
      result_status: asNullableString(caseStatus.result_status) ?? "unknown",
    },
    input: {
      citation_id: asNullableString(input.citation_id),
      court_address: asNullableString(input.court_address),
      defendant_full_name: asNullableString(input.defendant_full_name),
      violation_code: asNullableString(input.violation_code),
      violation_location: asNullableString(input.violation_location),
      violation_timestamp: asNullableString(input.violation_timestamp),
    },
    premium_status: {
      checkout_session_id: asNullableString(premiumStatus.checkout_session_id),
      locked: premiumStatus.locked !== false,
      payment_status: asNullableString(premiumStatus.payment_status),
      reason: asNullableString(premiumStatus.reason) ?? "Premium status unavailable.",
      unlocked: premiumStatus.unlocked === true,
      unlocks: asRecord(premiumStatus.unlocks) ?? {},
      product_entitlements: asRecord(premiumStatus.product_entitlements) ?? {},
    },
    product_entitlements: asRecord(raw.product_entitlements) ?? {},
    purchases: Array.isArray(raw.purchases) ? (raw.purchases as PurchaseItemResult[]) : [],
    summary: {
      citation_id: asNullableString(summary.citation_id),
      conclusion_preview: asNullableString(summary.conclusion_preview),
      created_at: asNullableString(summary.created_at),
      has_geo: summary.has_geo === true,
      has_weather: summary.has_weather === true,
      legal_strength_score: asNullableNumber(summary.legal_strength_score) ?? undefined,
      ticket_id: asNullableString(summary.ticket_id) ?? ((raw.analysis as AnalyzeTicketResult | undefined)?.ticket_id ?? ""),
      updated_at: asNullableString(summary.updated_at),
      violation_code: asNullableString(summary.violation_code),
    },
  };
};

export interface EntitlementStatusResult {
  current_period_end: string | null;
  last_checkout_session_id: string | null;
  premium_unlocked: boolean;
  source: string;
  status: string;
  stripe_customer_id: string | null;
  updated_at: string | null;
  user_id: string;
}

export interface CheckoutSessionCreateResult {
  checkout_session_id: string | null;
  checkout_url: string | null;
  expires_at: string | null;
  premium_unlocked: boolean;
  status: "created" | "already_unlocked";
  offer_type?: "normal" | "save_offer";
  product_key?: PremiumProductKey;
  price?: number | string | null;
  product_entitlements?: Record<string, boolean>;
}

export interface AttributionStateResult {
  first_touch: Record<string, unknown> | null;
  last_touch: Record<string, unknown> | null;
  ttl_days: number;
  rules: Record<string, string>;
}

export interface RevenueRecoveryStateResult {
  base_price: number;
  offer_price: number | null;
  offer_type: "save_offer" | null;
  product_key: PremiumProductKey;
  revenue_recovery_variant: "control" | "save29";
  save_offer_available: boolean;
  state: "NORMAL" | "DECLINED" | "SAVE_OFFER_SHOWN" | "SAVE_OFFER_DECLINED" | "CHECKOUT_PENDING" | "CHECKOUT_STARTED" | "PURCHASED";
  ticket_id: string;
}

export interface CheckoutSessionStatusResult {
  amount_total: number | null;
  checkout_session_id: string;
  completed_at: string | null;
  currency: string | null;
  expires_at: string | null;
  payment_status: string | null;
  premium_unlocked: boolean;
  product_key: PremiumProductKey | null;
  status: string;
  ticket_id: string | null;
}

export interface CheckoutFinalizeAccountResult {
  access_token: string | null;
  account_created: boolean;
  account_email: string | null;
  checkout_complete: boolean;
  checkout_session_id: string;
  expires_at: string | null;
  ownership_bound: boolean;
  reason: string | null;
  target_user_id: string | null;
}

export interface MagicLinkRequestResult {
  accepted: boolean;
  message: string;
  requested_at: string | null;
}

export interface MagicLinkVerifyResult {
  access_token: string;
  expires_at: string;
  token_type: string;
  user_id: string;
}

export interface MeCaseActionResult {
  download_court_pdf: string | null;
  open_analysis: string;
  open_defense_hub: string;
  open_records_request: string | null;
  prepare_mail_filing: string | null;
}

export interface MeCaseItemResult {
  actions: MeCaseActionResult;
  analyzed_at: string | null;
  case_type: string | null;
  current_status: string;
  records_request_status?: string | null;
  purchased_products: string[];
  score: number | null;
  ticket_id: string;
  mail_delivery_status?: string | null;
}

export interface MeCasesResult {
  items: MeCaseItemResult[];
  page: { limit: number; offset: number; total: number };
}

export interface MeOrderItemResult {
  amount_total: number | null;
  checkout_session_id: string;
  currency: string | null;
  product_key: string;
  product_name: string;
  purchased_at: string | null;
  status: string;
  ticket_id: string | null;
}

export interface MeOrdersResult {
  items: MeOrderItemResult[];
  page: { limit: number; offset: number; total: number };
}

export interface MeAccountResult {
  email: string;
  user_id: string;
}

export interface MeSessionResult {
  email: string | null;
  subject_type: "guest" | "account" | string;
  token_scope: string | null;
  user_id: string;
}

export interface MeProfileResult {
  account: MeAccountResult | null;
  session: MeSessionResult;
  stats: Record<string, number>;
}

type TrackedEventName =
  | "florida_state_selected"
  | "florida_analysis_started"
  | "florida_intake_started"
  | "florida_result_viewed"
  | "florida_checkout_started"
  | "florida_payment_confirmed"
  | "landing_view"
  | "landing_cta_clicked"
  | "landing_visit"
  | "intake_viewed"
  | "upload_cta_clicked"
  | "upload_started"
  | "upload_completed"
  | "upload_success"
  | "ocr_review_started"
  | "ocr_review_completed"
  | "ocr_completed"
  | "ocr_failed"
  | "narrative_started"
  | "intake_abandoned"
  | "intake_resumed"
  | "analysis_started"
  | "analyze_started"
  | "analysis_completed"
  | "analyze_completed"
  | "result_viewed"
  | "paywall_seen"
  | "paywall_viewed"
  | "pdf_requested"
  | "pdf_blocked_paywall"
  | "records_requested"
  | "cta_clicked"
  | "checkout_started"
  | "checkout_abandoned"
  | "payment_confirmed"
  | "payment_success"
  | "payment_failed"
  | "pdf_generated"
  | "pdf_downloaded"
  | "mail_started"
  | "mail_completed"
  | "mail_failed"
  | "recovery_purchase"
  | "speed_case_view"
  | "speed_cta_click"
  | "survey_pack_click"
  | "radar_pack_click"
  | "speed_checkout_started"
  | "hero_upload_started"
  | "hero_upload_completed"
  | "lead_captured"
  | "free_review_viewed"
  | "paid_options_viewed"
  | "product_selected"
  | "full_defense_selected"
  | "court_pdf_selected"
  | "records_pack_selected"
  | "mail_filing_selected"

export const resolveApiAssetUrl = (path: string | null | undefined): string | null => {
  if (!path) {
    return null;
  }
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const base = API_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "");
  if (!base) {
    return path;
  }
  return new URL(path, base).toString();
};

export const analyzeTicket = (payload: AnalyzeTicketRequest): Promise<AnalyzeTicketResult> =>
  request<AnalyzeTicketResult>({
    data: payload,
    method: "POST",
    path: "/api/analyze-ticket"
  });

export const processTicketPhoto = (file: File): Promise<OcrProcessResult> => {
  const formData = new FormData();
  formData.append("file", file);

  return request<OcrProcessResult>({
    data: formData,
    method: "POST",
    path: "/api/v1/ocr/process"
  });
};

export const getCitationDocument = (storageRef: string): Promise<Blob> =>
  request<Blob>({
    method: "GET",
    path: `/api/v1/uploads/citation/${storageRef.split("/").map(encodeURIComponent).join("/")}`,
    responseType: "blob",
  });

export interface LeadCaptureResult {
  accepted: boolean;
  lead_id: number;
  ticket_id: string;
  email_normalized: string;
  created_at: string;
  updated_at: string;
}

export const captureLead = (payload: {
  ticket_id: string;
  email: string;
  acquisition_session_id?: string;
  source?: string;
  funnel_stage?: string;
}): Promise<LeadCaptureResult> => request<LeadCaptureResult>({
  data: payload,
  method: "POST",
  path: "/api/v1/leads",
});

export const getTicketAnalysis = (ticketId: string): Promise<AnalyzeTicketResult> =>
  request<AnalyzeTicketResult>({
    method: "GET",
    path: `/api/analysis/${encodeURIComponent(ticketId)}`
  });

export const getCaseDetail = (ticketId: string): Promise<CaseDetailResult> =>
  request<CaseDetailResult>({
    method: "GET",
    path: `/api/v1/cases/${encodeURIComponent(ticketId)}`
  }).then((payload) => normalizeCaseDetailResult(payload));

export const downloadTicketPdf = (ticketId: string): Promise<Blob> =>
  request<Blob>({
    method: "GET",
    path: `/api/generate-pdf/${encodeURIComponent(ticketId)}`,
    responseType: "blob"
  });

export const sendCertifiedMail = (
  ticketId: string,
  addressCorrection?: MailAddressCorrectionPayload
): Promise<MailSendResult> =>
  request<MailSendResult>({
    data: addressCorrection,
    method: "POST",
    path: `/api/v1/mail/send-certified/${encodeURIComponent(ticketId)}`
  });

export const getMailStatus = (ticketId: string): Promise<MailStatusResult> =>
  request<MailStatusResult>({
    method: "GET",
    path: `/api/v1/mail/status/${encodeURIComponent(ticketId)}`
  });

export const getRecordsRequestArtifact = async (ticketId: string): Promise<RecordsRequestArtifactResult> => {
  try {
    return await request<RecordsRequestArtifactResult>({
      method: "GET",
      path: `/api/v1/artifacts/records-request/${encodeURIComponent(ticketId)}`
    });
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    if (status === 404) {
      throw new Error("Records request draft is temporarily unavailable for this case.");
    }
    throw error;
  }
};

export const downloadRecordsRequestPdf = (ticketId: string): Promise<Blob> =>
  request<Blob>({
    method: "GET",
    path: `/api/v1/artifacts/records-request/${encodeURIComponent(ticketId)}/download`,
    responseType: "blob"
  });

export const getRecordsWorkflowStatus = (ticketId: string): Promise<RecordsWorkflowStatusResult> =>
  request<RecordsWorkflowStatusResult>({
    method: "GET",
    path: `/api/v1/records-request/${encodeURIComponent(ticketId)}/status`
  });

export const generateRecordsWorkflowDraft = (ticketId: string): Promise<RecordsWorkflowStatusResult> =>
  request<RecordsWorkflowStatusResult>({
    method: "POST",
    path: `/api/v1/records-request/${encodeURIComponent(ticketId)}/draft`
  });

export const confirmRecordsWorkflowReview = (ticketId: string): Promise<RecordsWorkflowStatusResult> =>
  request<RecordsWorkflowStatusResult>({
    method: "POST",
    path: `/api/v1/records-request/${encodeURIComponent(ticketId)}/confirm-review`
  });

export const markRecordsWorkflowManualPending = (ticketId: string): Promise<RecordsWorkflowManualPendingResult> =>
  request<RecordsWorkflowManualPendingResult>({
    method: "POST",
    path: `/api/v1/records-request/${encodeURIComponent(ticketId)}/manual-pending`
  });

export const getEntitlementStatus = (): Promise<EntitlementStatusResult> =>
  request<EntitlementStatusResult>({
    method: "GET",
    path: "/api/v1/payments/entitlement"
  });

export const getTicketEntitlementStatus = (
  ticketId: string,
  productKey?: PremiumProductKey
): Promise<TicketEntitlementResult> =>
  request<TicketEntitlementResult>({
    method: "GET",
    path: `/api/v1/payments/entitlements/${encodeURIComponent(ticketId)}${
      productKey ? `?product_key=${encodeURIComponent(productKey)}` : ""
    }`
  });

export const createCheckoutSession = (
  ticketId: string | undefined,
  productKey: PremiumProductKey,
  offerType: "normal" | "save_offer" = "normal"
): Promise<CheckoutSessionCreateResult> => {
  if (!productKey) {
    throw new Error("productKey is required.");
  }
  return request<CheckoutSessionCreateResult>({
    data: {
      attribution: getCheckoutAttributionSnapshot(),
      offer_type: offerType,
      product_key: productKey,
      ticket_id: ticketId
    },
    method: "POST",
    path: "/api/v1/payments/checkout-session"
  });
};

export const captureAttribution = (
  touch: AttributionTouch,
  consent: "analytics" | "advertising"
): Promise<AttributionStateResult> =>
  request<AttributionStateResult>({
    data: { consent, touch },
    method: "POST",
    path: "/api/v1/attribution/capture"
  });

export const recordRevenueRecoveryDecline = (
  ticketId: string,
  productKey: PremiumProductKey,
  source: "paywall_close" | "checkout_cancel"
): Promise<RevenueRecoveryStateResult> => request({
  data: { product_key: productKey, source, ticket_id: ticketId },
  method: "POST",
  path: "/api/v1/payments/revenue-recovery/decline"
});

export const declineRevenueRecoveryOffer = (ticketId: string): Promise<RevenueRecoveryStateResult> => request({
  data: { decision: "declined", product_key: "court_pdf", ticket_id: ticketId },
  method: "POST",
  path: "/api/v1/payments/revenue-recovery/decision"
});

export const getCheckoutSessionStatus = (checkoutSessionId: string): Promise<CheckoutSessionStatusResult> =>
  request<CheckoutSessionStatusResult>({
    method: "GET",
    path: `/api/v1/payments/checkout-session/${encodeURIComponent(checkoutSessionId)}`
  });

export const finalizeCheckoutAccount = (checkoutSessionId: string): Promise<CheckoutFinalizeAccountResult> =>
  request<CheckoutFinalizeAccountResult>({
    method: "POST",
    path: `/api/v1/payments/checkout-session/${encodeURIComponent(checkoutSessionId)}/finalize-account`
  });

export const requestMagicLink = async (email: string): Promise<MagicLinkRequestResult> => {
  try {
    const response = await authClient.post<MagicLinkRequestResult>("/api/v1/auth/request-magic-link", { email });
    return response.data;
  } catch (error) {
    const detail = axios.isAxiosError(error) ? error.response?.data?.detail : null;
    if (typeof detail === "string" && detail.trim()) {
      throw new Error(detail);
    }
    throw new Error("Could not request magic link.");
  }
};

export const verifyMagicLink = async (token: string): Promise<MagicLinkVerifyResult> => {
  try {
    const response = await authClient.get<MagicLinkVerifyResult>(
      `/api/v1/auth/magic-link/verify?token=${encodeURIComponent(token)}`
    );
    return response.data;
  } catch (error) {
    const detail = axios.isAxiosError(error) ? error.response?.data?.detail : null;
    if (typeof detail === "string" && detail.trim()) {
      throw new Error(detail);
    }
    throw new Error("Invalid or expired magic link.");
  }
};

export const verifyCheckoutRecovery = async (token: string): Promise<CheckoutRecoveryVerifyResult> => {
  try {
    const response = await authClient.get<CheckoutRecoveryVerifyResult>(
      `/api/v1/payments/recovery/verify?token=${encodeURIComponent(token)}`
    );
    return response.data;
  } catch (error) {
    const detail = axios.isAxiosError(error) ? error.response?.data?.detail : null;
    throw new Error(typeof detail === "string" && detail.trim() ? detail : "Invalid or expired recovery link.");
  }
};

export const getMyCases = (): Promise<MeCasesResult> =>
  request<MeCasesResult>({
    method: "GET",
    path: "/api/v1/me/tickets"
  });

export const getMyOrders = (): Promise<MeOrdersResult> =>
  request<MeOrdersResult>({
    method: "GET",
    path: "/api/v1/me/payments"
  });

export const getMeProfile = (): Promise<MeProfileResult> =>
  request<MeProfileResult>({
    method: "GET",
    path: "/api/v1/me"
  });

export type FloridaStagingActionPlan = {
  status: "available" | "not_available" | "needs_review";
  eligibility_status: "eligible" | "excluded" | "needs_review" | "not_applicable";
  reason_codes: string[];
  official_sources: string[];
  deadline_instruction: string;
  options: Array<{ key: "pay" | "traffic_school" | "hearing"; label: string; caution: string; url: string }>;
  notices: string[];
  checkout_available?: boolean;
  county?: string | null;
  clerk_url?: string | null;
  products?: string[];
  selected_county?: string | null;
  classification_reason?: string | null;
};

export type FloridaStagingActionPlanPayload = Pick<
  AnalyzeTicketRequest,
  "source_state" | "florida_county" | "violation_code" | "violation_description" | "violation_location" | "court_name" | "court_address" | "citation_notes" | "ocr_raw_text"
>;

export const buildFloridaStagingActionPlan = (
  payload: FloridaStagingActionPlanPayload
): Promise<FloridaStagingActionPlan> =>
  request<FloridaStagingActionPlan>({
    data: payload,
    method: "POST",
    path: "/api/v1/strategy/staging/florida/miami-dade/action-plan"
  });

export const buildFloridaProductionActionPlan = (
  payload: FloridaStagingActionPlanPayload
): Promise<FloridaStagingActionPlan> =>
  request<FloridaStagingActionPlan>({
    data: payload,
    method: "POST",
    path: "/api/v1/strategy/florida/action-plan"
  });

export const createFloridaProductionCheckoutSession = (
  payload: FloridaStagingActionPlanPayload,
  productKey = "florida_full_defense"
): Promise<{ checkout_session_id: string | null; checkout_url: string | null; price: string | null }> =>
  request({
    data: { ...payload, product_key: productKey },
    method: "POST",
    path: "/api/v1/strategy/florida/checkout-session"
  });

export const trackFunnelEvent = (
  eventName: TrackedEventName,
  payload?: { metadata?: Record<string, unknown>; stage?: string; ticket_id?: string }
): Promise<void> =>
  request<void>({
    data: {
      event_name: eventName,
      metadata: payload?.metadata ?? {},
      stage: payload?.stage,
      ticket_id: payload?.ticket_id
    },
    method: "POST",
    path: "/api/v1/analytics/events"
  });
